import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart } from '@mui/x-charts/LineChart';
import { useSelector } from 'react-redux';
import { getFilterConfig } from '../../helpers/filterLabelGenerator';

const brlCurrencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const brlCompactFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function getRiskColorById(theme) {
  return {
    low: (theme.vars || theme).palette.warning.main,
    medium: theme.palette.mode === 'light' ? '#FB8C00' : '#FFB74D',
    high: (theme.vars || theme).palette.error.main,
  };
}

function RiskValueChart({ title, description, variation, value, labels: propLabels, transactions: propTransactions }) {
  const theme = useTheme();
  const filter = useSelector((state) => state.filter.value);
  const config = getFilterConfig(filter);
  const riskColorById = getRiskColorById(theme);

  const defaultTransactions = [
    {
      id: 'low',
      label: 'Baixa',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: [
        300, 900, 600, 1200, 1500, 1800, 2400, 2100, 2700, 3000, 1800, 3300,
        3600, 3900, 4200, 4500, 3900, 4800, 5100, 5400, 4800, 5700, 6000,
        6300, 6600, 6900, 7200, 7500, 7800, 8100,
      ],
    },
    {
      id: 'medium',
      label: 'Média',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: [
        300, 900, 600, 1200, 1500, 1800, 2400, 2100, 2700, 3000, 1800, 3300,
        3600, 3900, 4200, 4500, 3900, 4800, 5100, 5400, 4800, 5700, 6000,
        6300, 6600, 6900, 7200, 7500, 7800, 8100,
      ],
    },
    {
      id: 'high',
      label: 'Alta',
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: [
        500, 900, 700, 1400, 1100, 1700, 2300, 2000, 2600, 2900, 2300, 3200,
        3500, 3800, 4100, 4400, 2900, 4700, 5000, 5300, 5600, 5900, 6200,
        6500, 5600, 6800, 7100, 7400, 7700, 8000,
      ],
    },
  ];

  const labels = Array.isArray(propLabels) && propLabels.length
    ? propLabels
    : config.labels;

  const datumSource = Array.isArray(propTransactions) && propTransactions.length
    ? propTransactions
    : defaultTransactions;

  const datum = datumSource.map((transaction, index) => {
    const fallback = defaultTransactions[index] || defaultTransactions[0];
    const sourceData = Array.isArray(transaction.amount) && transaction.amount.length
      ? transaction.amount
      : transaction.quantity;
    const normalizedData = Array.from({ length: labels.length }, (_, itemIndex) => (
      sourceData && sourceData[itemIndex] !== undefined
        ? sourceData[itemIndex]
        : sourceData && sourceData.length
          ? sourceData[sourceData.length - 1]
          : 0
    ));

    return {
      id: transaction.id || fallback.id,
      label: transaction.label || fallback.label,
      showMark: false,
      curve: 'linear',
      stack: 'total',
      area: true,
      stackOrder: 'ascending',
      data: normalizedData,
      valueFormatter: (item) => brlCurrencyFormatter.format(Number(item?.value ?? item ?? 0)),
    };
  });

  const colorPalette = datum.map(
    (transaction) => riskColorById[transaction.id] || (theme.vars || theme).palette.warning.main,
  );

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack sx={{ justifyContent: 'space-between' }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: 'center', sm: 'flex-start' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h4" component="p">
              {value}
            </Typography>
            <Chip size="small" color="success" label={variation} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        </Stack>
        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: 'point',
              data: labels,
              // tickInterval: (index, i) => (i + 1) % 5 === 0,
              height: 24,
            },
          ]}
          yAxis={[{ width: 70, valueFormatter: (item) => brlCompactFormatter.format(Number(item ?? 0)) }]}
          series={datum}
          height={250}
          margin={{ left: 0, right: 20, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={{
            '& .MuiAreaElement-series-low': {
              fill: "url('#low')",
            },
            '& .MuiAreaElement-series-medium': {
              fill: "url('#medium')",
            },
            '& .MuiAreaElement-series-high': {
              fill: "url('#high')",
            },
          }}
        >
          <AreaGradient color={riskColorById.low} id="low" />
          <AreaGradient color={riskColorById.medium} id="medium" />
          <AreaGradient color={riskColorById.high} id="high" />
        </LineChart>
      </CardContent>
    </Card>
  );
};

RiskValueChart.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  variation: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  transactions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    quantity: PropTypes.arrayOf(PropTypes.number),
    amount: PropTypes.arrayOf(PropTypes.number),
  })),
}

export default RiskValueChart;
